// Config related store state and actions.

import axiosApiClient, { reloadPageOnBaseURLChange } from '@/api/axiosApiClient'
import { getSavedState, saveState } from '@/store/helpers'
import i18next from '@/utils/i18next'
import log from '@/utils/log'
import { getNearestCountryFromGeolocation } from '@/utils/geolocation'
import _ from 'lodash'

const ALL_DELIVERY_COLUMNS = [
	'status',
	'clientRefs',
	'pickupAddress',
	'pickupAt',
	'sender',
	'dropoffAddress',
	'deliveryBy',
	'recipient',
	'courier'
]
const DEFAULT_DELIVERY_COLUMNS = [
	'status',
	'clientRefs',
	'deliveryBy',
	'recipient',
	'courier'
]

// State
export const state = {
	// Get saved locale, if any.
	locale: getSavedState('config.locale') || 'en-HK',
	reasons: {},
	clients: [],
	deliveryColumns: [],
	locations: [],
	locales: [],
	regions: getSavedState('config.regions') || [],
	currentRegion: getSavedState('config.currentRegion') || null
}

// Getters
export const getters = {}

// Mutations
export const mutations = {
	SET_LOCALE(state, newValue) {
		i18next.changeLanguage(newValue) // Change i18next language.
		state.locale = newValue // Update current locale in store.
		saveState('config.locale', newValue) // Save to storage.
		log.changeLocale(newValue) // Change log context.
	},
	SET_REASONS(state, reasons) {
		state.reasons = reasons
	},
	SET_CLIENTS(state, clients) {
		state.clients = clients
	},
	SET_DELIVERY_COLUMNS(state, columns) {
		state.deliveryColumns = columns
	},
	SET_LOCATIONS(state, locations) {
		state.locations = locations
	},
	SET_LOCALES(state, locales) {
		state.locales = locales
	},
	SET_REGIONS(state, regions) {
		saveState('config.regions', regions)
		state.regions = regions
	},
	SET_CURRENT_REGION(state, currentRegion) {
		saveState('config.currentRegion', currentRegion)
		const dataCenter =
			_.get(currentRegion, 'datacenter', 'SIN') === 'SIN' ? 'sg' : 'br'

		saveState('config.dc', dataCenter)
		reloadPageOnBaseURLChange(dataCenter)
		state.currentRegion = currentRegion
	}
}

// Actions
export const actions = {
	init({ state }) {
		i18next.changeLanguage(state.locale) // Init i18next language.
	},

	// Updates current locale.
	changeLocale({ commit }, { newLocale }) {
		commit('SET_LOCALE', newLocale)
	},

	// Fetches failure & success reasons from form-field.
	async fetchReasons({ commit }) {
		try {
			/**
			 * Get form-fields with tag
			 * It's only limited to return 25 fields (and 10 by default), may need to write a new endpoint to fix and improve performance
			 * @param {string} tag
			 * @returns {Promise<any[]>}
			 */
			const getReasons = async tag => {
				const res = await axiosApiClient.get(
					`form-fields?tag=${tag}&limit=25`
				)
				return res.data
			}
			const successReasons = await getReasons('REASONS_SUCCESS')
			const failedReasons = await getReasons('REASONS_FAILURE')
			const crossdockDropOffReasons = await getReasons(
				'REASONS_CROSSDOCK_DROP_OFF'
			)
			const failurePickupReasons = await getReasons(
				'REASONS_FAILURE_PICKUP'
			)
			const successPickupReasons = await getReasons(
				'REASONS_SUCCESS_PICKUP'
			)
			const warehouseDropOffReasons = await getReasons(
				'REASONS_WAREHOUSE'
			)
			const reasonFields = [
				...successReasons,
				...failedReasons,
				...crossdockDropOffReasons,
				...failurePickupReasons,
				...successPickupReasons,
				...warehouseDropOffReasons
			]
			const reasons = reasonFields.reduce((acc, formField) => {
				const clientId = formField.clientId
					? formField.clientId
					: 'COMMON'
				formField.options.forEach(i => {
					const { tag, translations } = i
					if (!acc[clientId]) {
						acc[clientId] = {}
					}
					acc[clientId][tag] = translations
				})
				return acc
			}, {})
			commit('SET_REASONS', reasons)
		} catch (err) {
			log.error('Failed to fetch reasons', { category: 'API' }, err.stack)
		}
	},

	// Fetches clients.
	async fetchClients({ commit, dispatch, rootState }) {
		try {
			const { data } = await axiosApiClient.get('clients')
			commit('SET_CLIENTS', data)
			dispatch(
				'placeOrder/changeClient',
				{ clientId: data[0].id },
				{ root: true }
			)
			dispatch('dashboard/initClientFilter', {}, { root: true })

			const { clientId, roles } = rootState.auth.currentUser // get clientId and roles from auth store
			// As a default, display the original columns:
			let columns = DEFAULT_DELIVERY_COLUMNS
			if (roles.some(role => role === 'ADM' || role === 'OPS')) {
				// For ADM & OPS, display all columns.
				columns = ALL_DELIVERY_COLUMNS
			} else {
				// For CLT, display columns based on config (if any).
				const client = data.find(c => c.id === clientId)
				const { visibleDeliveryProperties } = client.attributes
				if (
					visibleDeliveryProperties &&
					visibleDeliveryProperties.length
				) {
					columns = visibleDeliveryProperties
				}
			}
			commit('SET_DELIVERY_COLUMNS', columns)
		} catch (err) {
			log.error('Failed to fetch clients', { category: 'API' }, err.stack)
		}
	},

	// Fetches locations.
	async fetchLocations({ commit }) {
		try {
			const { data } = (await axiosApiClient.get('/v1/locations')).data
			commit('SET_LOCATIONS', data)
		} catch (err) {
			log.error(
				'Failed to fetch locations',
				{ category: 'API' },
				err.stack
			)
		}
	},

	// Fetches locales.
	async fetchLocales({ commit }) {
		try {
			const { data } = await axiosApiClient.get('locales', {
				params: {
					limit: 100
				}
			})
			commit('SET_LOCALES', data)
		} catch (err) {
			log.error('Failed to fetch locales', { category: 'API' }, err.stack)
		}
	},

	async fetchRegions({ state, commit }) {
		// NOTE: avoid calling in ui test for saving api quotas
		if (process.env.NODE_ENV === 'test') {
			return
		}

		// Disabled - always request region data, details: https://lalamove.atlassian.net/browse/LLP-31602
		// if (state.regions.length > 0 && state.currentRegion) {
		// 	return
		// }

		const { defaultRegion, data: regions } = _.get(
			await axiosApiClient.get('/v1/regions'),
			'data',
			{}
		)
		const currentRegion =
			defaultRegion ||
			(await getNearestCountryFromGeolocation(regions)) ||
			_.find(regions, { id: 'HK' }) // fallback to HK

		commit('SET_REGIONS', regions)
		commit('SET_CURRENT_REGION', currentRegion)
	},

	setCurrentRegion({ commit }, region) {
		commit('SET_CURRENT_REGION', region)
	}
}
